import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Video, Play, Lock, Star, Clock, Filter, Loader, 
  ExternalLink, ChevronRight, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SellerCourses = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState({});
  const [userTier, setUserTier] = useState(1);
  const [userPlan, setUserPlan] = useState('premium');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFeatured, setFilterFeatured] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterFeatured) params.append('featured', 'true');
      
      const response = await axios.get(
        `${API_URL}/seller/courses?${params.toString()}`,
        { withCredentials: true }
      );
      setCourses(response.data.courses);
      setUserTier(response.data.user_tier);
      setUserPlan(response.data.user_plan);
    } catch (error) {
      toast.error('Erro ao carregar cursos');
    }
  }, [filterCategory, filterFeatured]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCategories();
      await fetchCourses();
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchCourses]);

  const handleWatchCourse = async (course) => {
    if (!course.has_access) {
      toast.error(`Este curso requer plano ${course.tier_label} ou superior`);
      return;
    }

    try {
      // Buscar curso completo (incrementa views)
      const response = await axios.get(
        `${API_URL}/seller/courses/${course.course_id}`,
        { withCredentials: true }
      );
      setSelectedCourse(response.data);
      setIsVideoOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar curso');
    }
  };

  const getVideoEmbedUrl = (url) => {
    // YouTube
    const youtubeMatch = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url?.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return url;
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      1: 'bg-gray-100 text-gray-700',
      2: 'bg-blue-100 text-blue-700',
      3: 'bg-green-100 text-green-700',
      4: 'bg-yellow-100 text-yellow-700',
      5: 'bg-orange-100 text-orange-700',
      6: 'bg-purple-100 text-purple-700',
      7: 'bg-pink-100 text-pink-700'
    };
    return colors[tier] || colors[1];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader className="w-12 h-12 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="seller-courses-title">
            Cursos Needi
          </h1>
          <p className="text-lg text-gray-600">
            Aprenda e evolua seu negócio com nossos cursos exclusivos
          </p>
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-gray-600">Seu plano:</span>
            <span className={`text-sm px-3 py-1 rounded-full font-medium ${getTierBadgeColor(userTier)}`}>
              {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} (Tier {userTier})
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-4 h-4 text-gray-500" />
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              data-testid="filter-category"
            >
              <option value="">Todas as categorias</option>
              {categories.categories?.map(cat => (
                <option key={cat} value={cat}>
                  {categories.labels?.[cat]}
                </option>
              ))}
            </select>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filterFeatured}
                onChange={(e) => setFilterFeatured(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Apenas destacados</span>
            </label>
          </div>
        </div>

        {/* Courses Grid */}
        {courses.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum curso disponível
            </h3>
            <p className="text-gray-600">
              Novos cursos serão adicionados em breve!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div
                key={course.course_id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow ${
                  !course.has_access ? 'opacity-75' : ''
                }`}
                data-testid={`course-${course.course_id}`}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                      <Video className="w-12 h-12 text-white opacity-50" />
                    </div>
                  )}
                  
                  {/* Overlay for locked courses */}
                  {!course.has_access && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-center text-white">
                        <Lock className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm font-medium">Requer {course.tier_label}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Featured badge */}
                  {course.is_featured && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3 fill-white" />
                        Destaque
                      </span>
                    </div>
                  )}
                  
                  {/* Play button */}
                  {course.has_access && (
                    <button
                      onClick={() => handleWatchCourse(course)}
                      className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/30"
                    >
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <Play className="w-8 h-8 text-indigo-600 ml-1" />
                      </div>
                    </button>
                  )}
                </div>
                
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                    {course.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {course.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded">
                      {categories.labels?.[course.category]}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${getTierBadgeColor(course.min_tier)}`}>
                      {course.tier_label}+
                    </span>
                    {course.duration_minutes && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {course.duration_minutes}min
                      </span>
                    )}
                  </div>

                  <Button
                    onClick={() => handleWatchCourse(course)}
                    className={`w-full ${
                      course.has_access 
                        ? 'bg-indigo-600 hover:bg-indigo-700' 
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!course.has_access}
                    data-testid={`watch-course-${course.course_id}`}
                  >
                    {course.has_access ? (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Assistir
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Bloqueado
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video Dialog */}
      <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle data-testid="video-dialog-title">
              {selectedCourse?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="aspect-video w-full">
            {selectedCourse?.video_url && (
              <iframe
                src={getVideoEmbedUrl(selectedCourse.video_url)}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedCourse.title}
              />
            )}
          </div>
          
          <div className="p-4 border-t">
            <p className="text-gray-600">{selectedCourse?.description}</p>
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {selectedCourse?.duration_minutes || '?'} minutos
              </span>
              <span>•</span>
              <span>{categories.labels?.[selectedCourse?.category]}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
